import { seriesKey } from '../../shared/utils/vendor-series-key.util';
import type { SeriesObservation } from '../observation/observation';
import { resolveRevenue } from './revenue';

/** Sum of unitsSold across all observations; null or non-positive units are excluded. */
export function sumVolume(obs: readonly SeriesObservation[]): number {
  let total = 0;
  for (const o of obs) {
    const units = o.signal.unitsSold;
    if (units !== null && units > 0) total += units;
  }
  return total;
}

/** Sum of canonically resolved revenue across all observations. */
export function sumValue(obs: readonly SeriesObservation[]): number {
  let total = 0;
  for (const o of obs) {
    const rev = resolveRevenue(o);
    if (rev !== null) total += rev;
  }
  return total;
}

/** Units sold on observations whose revenue cannot be resolved (no charged or list price). */
export function sumUnpricedVolume(obs: readonly SeriesObservation[]): number {
  let total = 0;
  for (const o of obs) {
    const units = o.signal.unitsSold;
    if (units !== null && units > 0 && resolveRevenue(o) === null) total += units;
  }
  return total;
}

/**
 * Aggregate ASP: total revenue / total units, rounded to 1 decimal place.
 * Returns null when total units or total revenue are not positive.
 */
export function computeAsp(obs: readonly SeriesObservation[]): number | null {
  const volume = sumVolume(obs);
  const value = sumValue(obs);
  if (volume <= 0 || value <= 0) return null;
  return Math.round((value / volume) * 10) / 10;
}

/**
 * Aggregates unitsSold per vendor series key across all observations.
 * Vendor key is seriesKey(vendor, channel). Observations with null or
 * non-positive unitsSold are excluded.
 */
export function computeVendorTotals(obs: readonly SeriesObservation[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const o of obs) {
    const units = o.signal.unitsSold;
    if (units !== null && units > 0) {
      const k = seriesKey(o.vendor, o.channel);
      totals.set(k, (totals.get(k) ?? 0) + units);
    }
  }
  return totals;
}
